import { FormEvent, useState } from "react";
import { PaymentElement } from "@stripe/react-stripe-js";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import { useTranslation } from "next-i18next";
import {
  ArrowRightIcon,
  WarningIcon,
  PoweredByStripeIcon,
} from "@talladega/icons";
import { Button } from "../Button";
import { Form } from "../FormInputs";

interface FormData {
  note: string;
}

type Props = {
  errorMessage: string;
  submitBtnMessage: string;
  cancelBtnMessage?: string | undefined;
  isLoading: boolean;
  presentation?: "modal" | "page";
  onSubmitPayment: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onCancel?: () => void | undefined;
  billingDetails?: { postalCode: string | undefined }; // Only care about postal code for now.
  showInternalNote?: boolean;
};

export const StripePaymentForm = ({
  errorMessage,
  submitBtnMessage,
  cancelBtnMessage,
  isLoading,
  presentation = "page",
  onSubmitPayment,
  onCancel,
  billingDetails,
  showInternalNote = false,
}: Props) => {
  const [isFrameReady, setIsFrameReady] = useState(false);

  const { register } = useForm<FormData>();
  const { t } = useTranslation(["common"]);

  return (
    <form onSubmit={onSubmitPayment}>
      {errorMessage && (
        <p className="mb-3 text-error-400 flex flex-row items-center gap-3">
          <WarningIcon /> {errorMessage}
        </p>
      )}
      <PaymentElement
        onReady={() => setIsFrameReady(true)}
        options={{
          layout: "accordion",
          defaultValues: {
            billingDetails: {
              address: { postal_code: billingDetails?.postalCode },
            },
          },
        }}
      />

      {showInternalNote && (
        <div className="mt-4 bg-gray-25 p-3 rounded-xl">
          <Form.TextArea
            label={t("common:internal_note") || undefined}
            className="pb-3"
            placeholder={t("common:enter_details_here") || undefined}
            {...register("note")}
          />
          <p className="text-sm font-semibold text-primary-deep text-left">
            {t("common:all_notes_are_for_internal_use_only")}
          </p>
        </div>
      )}
      {isFrameReady && (
        <>
          <Button
            className={classNames("mt-6 w-full", {
              "sm:w-fit": presentation === "page",
            })}
            disabled={isLoading}
            type="submit"
            rightIcon={<ArrowRightIcon />}
          >
            {submitBtnMessage}
          </Button>
          {onCancel && (
            <Button
              fill="link"
              className="underline hover:no-underline"
              onClick={onCancel}
            >
              {cancelBtnMessage}
            </Button>
          )}
          <div
            className={classNames("flex justify-center", {
              "sm:justify-start": presentation === "page",
            })}
          >
            <PoweredByStripeIcon className="w-28 h-12" />
          </div>
        </>
      )}
    </form>
  );
};

export default {};
